import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PaginationBar from '../components/PaginationBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { formatMoney } from '../lib/currency';
import {
  Calendar, MapPin, Users, Clock, Building2, Globe,
  GraduationCap, Briefcase, Plus, Search, Loader2, ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EVENTS_PAGE_SIZE = 12;

const typeIcons = { job_fair: Briefcase, open_day: GraduationCap, company_event: Building2, webinar: Globe };
const typeColors = { job_fair: 'bg-blue-100 text-blue-700', open_day: 'bg-emerald-100 text-emerald-700', company_event: 'bg-purple-100 text-purple-700', webinar: 'bg-amber-100 text-amber-700' };

export default function EventsPage() {
  const { token, isAuthenticated, isEmployer, isEventHost, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', event_type: 'job_fair',
    location: '', date: '', time: '', max_attendees: 100
  });

  // Changing the event type starts again from page 1.
  useEffect(() => { setPage(1); }, [filter]);
  useEffect(() => { fetchEvents(); }, [filter, page]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      // The API returns no total, so ask for one extra row to learn whether a next page exists.
      const res = await axios.get(`${API_URL}/api/events`, {
        params: { ...(filter !== 'all' ? { event_type: filter } : {}), skip: (page - 1) * EVENTS_PAGE_SIZE, limit: EVENTS_PAGE_SIZE + 1 },
      });
      setHasMore(res.data.length > EVENTS_PAGE_SIZE);
      setEvents(res.data.slice(0, EVENTS_PAGE_SIZE));
    } catch {
      setEvents([]);
      setHasMore(false);
    } finally { setLoading(false); }
  };

  // A stale page beyond the last one falls back to the first page.
  useEffect(() => {
    if (!loading && page > 1 && events.length === 0) setPage(1);
  }, [loading, events.length, page]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/events`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event created!');
      setShowCreate(false);
      fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create event'); }
    finally { setCreating(false); }
  };

  const handleRegister = async (event) => {
    if (!isAuthenticated) { toast.error('Please log in to register'); return; }
    const isPaid = !event.is_free && Number(event.price) > 0;
    try {
      if (isPaid) {
        const res = await axios.post(`${API_URL}/api/events/${event.id}/checkout`, {
          origin_url: window.location.origin
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.url) window.location.href = res.data.url;
        return;
      }
      await axios.post(`${API_URL}/api/events/${event.id}/register`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Registered successfully!');
      fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Registration failed'); }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="events-page">
      <div className="bg-gradient-to-r from-[#003366] to-[#004080] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <Badge className="bg-white/10 text-white border-white/20 mb-4">
                <Calendar className="w-3 h-3 mr-1" />Events
              </Badge>
              <h1 className="text-4xl font-bold mb-4">Upcoming Events</h1>
              <p className="text-white/80 text-lg">Job fairs, university open days, and career events across the UK</p>
            </div>
            {isAuthenticated && (isEmployer || isEventHost || isAdmin) && (
              <Button onClick={() => setShowCreate(true)} className="bg-[#E11D48] hover:bg-[#E11D48]/90" data-testid="create-event-btn">
                <Plus className="w-4 h-4 mr-2" />Create Event
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-3 mb-8 flex-wrap">
          {[{ v: 'all', l: 'All Events' }, { v: 'job_fair', l: 'Job Fairs' }, { v: 'open_day', l: 'Open Days' }, { v: 'company_event', l: 'Company Events' }, { v: 'webinar', l: 'Webinars' }].map(f => (
            <Button key={f.v} variant={filter === f.v ? 'default' : 'outline'} size="sm"
              onClick={() => setFilter(f.v)}
              className={filter === f.v ? 'bg-[#003366]' : ''}
              data-testid={`filter-${f.v}`}
            >{f.l}</Button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#003366]" /></div>
        ) : events.length === 0 ? (
          <Card><CardContent className="p-12 text-center">
            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No events found</h3>
            <p className="text-muted-foreground">Check back soon for upcoming events</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map(event => {
              const Icon = typeIcons[event.event_type] || Calendar;
              const color = typeColors[event.event_type] || 'bg-gray-100 text-gray-700';
              return (
                <Card key={event.id} className="hover:shadow-xl transition-all overflow-hidden" data-testid={`event-card-${event.id}`}>
                  <div className="h-32 bg-gradient-to-br from-[#003366] to-[#004080] flex items-center justify-center">
                    <Icon className="w-12 h-12 text-white/30" />
                  </div>
                  <CardContent className="p-5">
                    <Badge className={`mb-3 ${color}`}>
                      {event.event_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                    <h3 className="font-semibold text-lg mb-2">{event.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{event.description}</p>
                    <div className="space-y-2 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-2"><MapPin className="w-4 h-4" />{event.location}</div>
                      <div className="flex items-center gap-2"><Calendar className="w-4 h-4" />{event.date} at {event.time}</div>
                      <div className="flex items-center gap-2"><Users className="w-4 h-4" />{event.attendees_count || 0} / {event.max_attendees || '∞'} registered</div>
                    </div>
                    <Button onClick={() => handleRegister(event)} className="w-full bg-[#003366] hover:bg-[#003366]/90" data-testid={`register-event-${event.id}`}>
                      {!event.is_free && Number(event.price) > 0 ? `Register — ${formatMoney(event.price, event.currency)}` : 'Register Now'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {!loading && events.length > 0 && (
          <PaginationBar page={page} pageSize={EVENTS_PAGE_SIZE} hasMore={hasMore} onPageChange={setPage} />
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Event</DialogTitle>
            <DialogDescription>Fill in the event details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Event Title</Label>
              <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Annual Tech Job Fair" data-testid="event-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select value={formData.event_type} onValueChange={v => setFormData({ ...formData, event_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="job_fair">Job Fair</SelectItem>
                  <SelectItem value="open_day">Open Day</SelectItem>
                  <SelectItem value="company_event">Company Event</SelectItem>
                  <SelectItem value="webinar">Webinar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Time</Label><Input type="time" value={formData.time} onChange={e => setFormData({ ...formData, time: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Location</Label><Input value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} placeholder="London, UK or Online" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#E11D48] hover:bg-[#E11D48]/90">
              {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
